import { useMutation, useQuery, useSubscription } from '@vue/apollo-composable';
import gql from 'graphql-tag';
import { defineStore } from 'pinia';
import { reactive } from 'vue';
import { UPDATE_ADYEN_APP_STATE } from '@/modules/register/salonized-pay/dashboard/graphql';
import productFruits from '@/product-fruits';
import type { AdyenAppState, PosTerminalStatus } from '@/types';
import { AdyenAppVerificationStatus } from '@/types';
import { useLocationsStore } from './locations';

export const useAdyenStore = defineStore('adyen', () => {
  type ExtendedAdyenAppState = Omit<AdyenAppState, 'id'> & { loading: boolean };

  const adyenAppState = reactive<ExtendedAdyenAppState>({
    adyenLegalEntityId: '',
    createdAt: '',
    industryCode: '',
    loading: false,
    terminalModel: undefined,
    verificationModalDismissedAt: '',
    verificationStatus: AdyenAppVerificationStatus.Pending,
    terminalTippingEnabled: false,
    transferInstruments: [],
  });

  const terminals = ref<
    {
      id: string;
      location: {
        id: number;
      };
      name: string | null;
      status: PosTerminalStatus;
      terminalUid: string;
    }[]
  >([]);

  const locationTerminals = computed(() => {
    const { locationId } = useLocationsStore();
    const connectedTerminals = terminals.value.filter(
      (terminal) => terminal.status === 'CONNECTED',
    );
    return connectedTerminals.filter(
      (terminal) => terminal.location?.id === locationId,
    );
  });

  const setData = (data: AdyenAppState) => {
    Object.assign(adyenAppState, data);
  };

  const adyenAppStateLoaded = ref(false);

  const { onResult, refetch: refetchAdyenAppState } = useQuery(gql`
    query getAdyenAppState {
      adyenAppState {
        adyenLegalEntityId
        createdAt
        industryCode
        terminalModel
        verificationModalDismissedAt
        verificationStatus
        terminalTippingEnabled
        transferInstruments {
          id
          accountNumberLast4
          bankName
        }
      }
    }
  `);

  onResult(({ data: { adyenAppState } }) => {
    setData(adyenAppState);

    productFruits.updateData({
      company_has_salonized_pay: adyenAppState?.verificationStatus === 'VALID',
    });

    if (
      adyenAppState?.verificationStatus === 'VALID' ||
      adyenAppState?.verificationStatus === 'INVALID'
    ) {
      const {
        result: terminalsResult,
        loading: terminalsLoading,
        error: terminalsError,
      } = useQuery(
        gql`
          query getPosTerminals {
            posTerminals {
              id
              location {
                id
              }
              name
              status
              terminalUid
            }
          }
        `,
        null,
        {
          fetchPolicy: 'cache-first',
        },
      );

      watch(
        [terminalsLoading, terminalsResult, terminalsError],
        ([isLoading, data, queryError]) => {
          if (isLoading || (!data && !queryError)) {
            return;
          }

          if (data?.posTerminals) {
            terminals.value = data.posTerminals;
          }

          adyenAppStateLoaded.value = true;
        },
        { immediate: true },
      );
    } else {
      adyenAppStateLoaded.value = true;
    }
  });

  const startAdyenVerificationSubscription = () => {
    const { onResult } = useSubscription(gql`
      subscription adyenVerificationStatus {
        adyenVerificationStatus {
          verificationStatus
          terminalModel
          adyenLegalEntityId
        }
      }
    `);

    onResult(({ data: { adyenVerificationStatus } }) => {
      if (adyenVerificationStatus) {
        setData(adyenVerificationStatus);
      }
    });
  };

  const updateTerminalTipping = async (enabled: boolean) => {
    const { mutate: updateAdyenAppState } = useMutation(UPDATE_ADYEN_APP_STATE);

    await updateAdyenAppState({
      input: { terminalTippingEnabled: enabled },
    });
    await refetchAdyenAppState();
  };

  const hasTerminals = computed(
    () => terminals.value && !!terminals.value.length,
  );

  return {
    startAdyenVerificationSubscription,
    refetchAdyenAppState,
    adyenAppState,
    terminals,
    locationTerminals,
    adyenAppStateLoaded,
    updateTerminalTipping,
    hasTerminals,
  };
});
